import React, { useEffect, useState } from 'react';
import { Snackbar, Alert, Box, Typography, CircularProgress } from '@mui/material';
import { WifiOff, CloudSync, CheckCircle } from '@mui/icons-material';
import { useNetwork } from '../context/NetworkContext';
import { offlineManager } from '../api/OfflineManager';
import api from '../api/axios';

const OfflineIndicator = () => {
    const { isOnline } = useNetwork();
    const [showOnline, setShowOnline] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncCount, setSyncCount] = useState(0);
    const [failedCount, setFailedCount] = useState(0);

    useEffect(() => {
        if (isOnline) {
            setShowOnline(true);
            const timer = setTimeout(() => setShowOnline(false), 3000);
            
            // Trigger sync when coming online
            syncOfflineData();

            return () => clearTimeout(timer);
        }
    }, [isOnline]);

    const syncOfflineData = async () => {
        const queue = await offlineManager.getQueuedRequests();
        if (queue.length === 0) return;

        setSyncing(true);
        setSyncCount(queue.length);
        setFailedCount(0);

        console.log(`Syncing ${queue.length} offline actions...`);

        let failed = 0;
        // Process queue sequentially to maintain order
        for (const request of queue) {
            try {
                // We need to bypass the offline interceptor check, 
                // but since we are online now, the standard axios flow works.
                // We just need to reconstruct the call.
                const resp = await api({
                    url: request.url,
                    method: request.method,
                    data: request.data,
                    params: request.params,
                    headers: request.headers
                });

                // A queued response means we went back offline mid-sync —
                // stop, leave the rest queued.
                if (resp?.isQueued) break;

                // Remove from queue on success
                await offlineManager.removeRequest(request.id);
                setSyncCount(prev => prev - 1);
            } catch (err) {
                // Surfaced — silent console-swallowing was how offline
                // actions got lost while the app looked synced.
                console.error('Failed to sync request:', request, err);
                failed += 1;
                setFailedCount(failed);
            }
        }

        setSyncing(false);
    };

    if (!isOnline) {
        return (
            <Snackbar open={true} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <Alert 
                    icon={<WifiOff fontSize="inherit" />} 
                    severity="warning"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    You are offline. Changes will be saved locally.
                </Alert>
            </Snackbar>
        );
    }

    if (syncing) {
        return (
            <Snackbar open={true} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <Alert 
                    icon={<CloudSync fontSize="inherit" />} 
                    severity="info"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} color="inherit" />
                        <Typography variant="body2">
                            Syncing data... {syncCount > 0 && `(${syncCount} remaining)`}
                        </Typography>
                    </Box>
                </Alert>
            </Snackbar>
        );
    }

    if (failedCount > 0) {
        return (
            <Snackbar open={true} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <Alert
                    icon={<WifiOff fontSize="inherit" />}
                    severity="error"
                    variant="filled"
                    sx={{ width: '100%' }}
                    action={
                        <Box component="button"
                             onClick={() => { setFailedCount(0); syncOfflineData(); }}
                             sx={{ background: 'none', border: 'none', color: 'inherit',
                                   fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                            RETRY
                        </Box>
                    }
                >
                    {failedCount} offline action(s) couldn't sync — tap RETRY.
                </Alert>
            </Snackbar>
        );
    }

    return (
        <Snackbar 
            open={showOnline} 
            autoHideDuration={3000} 
            onClose={() => setShowOnline(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
            <Alert 
                icon={<CheckCircle fontSize="inherit" />} 
                severity="success" 
                variant="filled"
                sx={{ width: '100%' }}
            >
                Back online
            </Alert>
        </Snackbar>
    );
};

export default OfflineIndicator;
